import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { API_BASE_URL } from '../infra/apiConfig';

// =================================================================
// FUNÇÕES DE FORMATAÇÃO DE MÁSCARA
// =================================================================
const formatCPF = (value) => {
  if (!value) return "";
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .substring(0, 14);
};

const formatTelefone = (value) => {
  if (!value) return "";
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .substring(0, 15);
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/perfil`, getAuthHeaders());
        const userData = response.data;

        Object.keys(userData).forEach(key => {
          if (key === 'data_nascimento') {
            setValue('dataNascimento', userData[key].split('T')[0]);
          } else {
            const camelCaseKey = key.replace(/_([a-z])/g, g => g[1].toUpperCase());
            setValue(camelCaseKey, userData[key]);
          }
        });
      } catch (error) {
        console.error("Erro ao buscar dados do perfil:", error);
        alert("Não foi possível carregar seus dados. Tente fazer o login novamente.");
        navigate('/');
      }
    };
    fetchUserData();
  }, [setValue, navigate]);

  const onUpdate = async (data) => {
    if (window.confirm('Deseja realmente salvar as alterações?')) {
      try {
        const response = await axios.put(`${API_BASE_URL}/perfil`, data, getAuthHeaders());
        const novoToken = response.data.token;

        if (novoToken) {
          localStorage.setItem('token', novoToken);
          console.log("Token atualizado com sucesso!");
        }

        alert('Perfil atualizado com sucesso!');
        navigate('/inscricao');
      } catch (error) {
        console.error("Erro ao atualizar o perfil:", error);
        alert(`Erro: ${error.response?.data?.error || 'Não foi possível atualizar o perfil.'}`);
      }
    }
  };

  const onDelete = async () => {
    if (window.confirm('TEM CERTEZA que deseja excluir sua conta? Esta ação é irreversível.')) {
      try {
        await axios.delete(`${API_BASE_URL}/perfil`, getAuthHeaders());
        alert('Sua conta foi deletada com sucesso.');
        localStorage.removeItem('token');
        navigate('/');
      } catch (error) {
        console.error("Erro ao deletar a conta:", error);
        alert(`Erro: ${error.response?.data?.error || 'Não foi possível deletar a conta.'}`);
      }
    }
  };

  // Função para aplicar a máscara enquanto o usuário digita
  const handleInputChange = (event, formatFunction) => {
    event.target.value = formatFunction(event.target.value);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-lg mb-4">
        <Link to="/inscricao" className="font-medium text-blue-600 hover:text-blue-500">
          &larr; Voltar para a Inscrição
        </Link>
      </div>

      <div className="w-full max-w-lg p-8 space-y-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-900">
          Editar Perfil
        </h2>

        <form className="space-y-4" onSubmit={handleSubmit(onUpdate)}>
          <h3 className="text-lg font-medium text-gray-800 border-b pb-2">Dados Pessoais</h3>
          <div>
            <label htmlFor="nomeCompleto" className="block text-sm font-medium text-gray-700">Nome completo</label>
            <input id="nomeCompleto" type="text" className="mt-1 w-full p-2 border rounded-md" {...register("nomeCompleto", { required: true })} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="dataNascimento" className="block text-sm font-medium text-gray-700">Data de nascimento</label>
              <input id="dataNascimento" type="date" className="mt-1 w-full p-2 border rounded-md" {...register("dataNascimento", { required: true })} />
            </div>
            <div>
              <label htmlFor="cpf" className="block text-sm font-medium text-gray-700">CPF</label>
              <input
                id="cpf"
                type="text"
                className="mt-1 w-full p-2 border rounded-md"
                maxLength="14"
                onInput={(e) => handleInputChange(e, formatCPF)}
                {...register("cpf", { required: true })}
              />
            </div>
          </div>
          <div>
            <label htmlFor="telefone" className="block text-sm font-medium text-gray-700">Telefone</label>
            <input
              id="telefone"
              type="tel"
              className="mt-1 w-full p-2 border rounded-md"
              maxLength="15"
              onInput={(e) => handleInputChange(e, formatTelefone)}
              {...register("telefone", { required: true })}
            />
          </div>

          <h3 className="text-lg font-medium text-gray-800 border-b pt-4 pb-2">Dados Escolares</h3>
          <div>
            <label htmlFor="escola" className="block text-sm font-medium text-gray-700">Escola</label>
            <input id="escola" type="text" className="mt-1 w-full p-2 border rounded-md" {...register("escola", { required: true })} />
          </div>
          <div>
            <label htmlFor="serie" className="block text-sm font-medium text-gray-700">Série</label>
            <select id="serie" className="mt-1 w-full p-2 border rounded-md" {...register("serie", { required: true })}>
              <option value="8_fundamental">8º Ano - Ensino Fundamental</option>
              <option value="9_fundamental">9º Ano - Ensino Fundamental</option>
              <option value="1_medio">1º Ano - Ensino Médio</option>
              <option value="2_medio">2º Ano - Ensino Médio</option>
              <option value="3_medio">3º Ano - Ensino Médio</option>
            </select>
          </div>

          <div>
            <label htmlFor="categoria" className="block text-sm font-medium text-gray-700">Categoria</label>
            <select
              id="categoria"
              className={`mt-1 w-full p-2 border ${errors.categoria ? 'border-red-500' : 'border-gray-300'} rounded-md`}
              {...register("categoria", { required: "Selecione sua categoria." })}
            >
              <option value="">Selecione sua categoria</option>
              <option value="EX ALUNO DO PASSAPORTE DIGITAL">EX-ALUNO DO PASSAPORTE DIGITAL</option>
              <option value="ALUNO ATUAL DO PASSAPORTE DIGITAL">ALUNO ATUAL DO PASSAPORTE DIGITAL</option>
              <option value="ESTUDANTE DA REDE DE ESCOLAS DO MUNICÍPIO DE LAGOA DE ITAENGA">
                ESTUDANTE DA REDE DE ESCOLAS DO MUNICÍPIO DE LAGOA DE ITAENGA
              </option>
            </select>
            {errors.categoria && <p className="text-sm text-red-600">{errors.categoria.message}</p>}
          </div>


          <h3 className="text-lg font-medium text-gray-800 border-b pt-4 pb-2">Dados de Acesso</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">E-mail</label>
              <input id="email" type="email" className="mt-1 w-full p-2 border rounded-md" {...register("email", { required: true })} />
            </div>
            <div>
              <label htmlFor="usuario" className="block text-sm font-medium text-gray-700">Usuário</label>
              <input id="usuario" type="text" className="mt-1 w-full p-2 border rounded-md" {...register("usuario", { required: true })} />
            </div>
          </div>
          <p className="text-sm text-gray-500 text-center pt-2">Para alterar sua senha, entre em contato com o suporte.</p>

          <div className="space-y-4 pt-4">
            <button type="submit" className="w-full px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700">
              Salvar Alterações
            </button>
            <button type="button" onClick={onDelete} className="w-full px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700">
              Excluir Minha Conta
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
